import { useQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { listMobileConversations } from '@/lib/messaging/messaging-mobile-client';
import { useAuth } from '@/contexts/AuthContext';

// Mobile scheduling inbox. Separate query-key namespace from the web/kiosk
// messaging hooks (['messaging',...] / ['kiosk-messaging',...]) so the surfaces
// never cross-contaminate in the React Query cache — mirrors the kiosk hook split.
const FOCUSED_INTERVAL_MS = 30_000;
const HIDDEN_INTERVAL_MS = 120_000;

export const mobileConversationsQueryKey = ['mobile-messaging', 'conversations'] as const;

export const useMobileConversations = () => {
  const { profile } = useAuth();
  const profileId = profile?.id;

  const [hidden, setHidden] = useState<boolean>(
    typeof document !== 'undefined' ? document.visibilityState === 'hidden' : false,
  );

  useEffect(() => {
    if (typeof document === 'undefined') return;
    const onVis = () => setHidden(document.visibilityState === 'hidden');
    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
  }, []);

  const refetchInterval = hidden ? HIDDEN_INTERVAL_MS : FOCUSED_INTERVAL_MS;

  const query = useQuery({
    queryKey: mobileConversationsQueryKey,
    enabled: !!profileId,
    refetchInterval,
    queryFn: async () => {
      const { data, error } = await listMobileConversations();
      if (error) throw new Error(error.message);
      return data ?? [];
    },
  });

  const activeConversations = useMemo(
    () => (query.data ?? []).filter((c) => !c.archived_at),
    [query.data],
  );

  const totalUnreadCount = useMemo(
    () => (query.data ?? []).reduce((sum, c) => sum + (c.unread_count ?? 0), 0),
    [query.data],
  );

  return { ...query, activeConversations, totalUnreadCount };
};
